import { useCallback } from "react"
import { type ConnectDropTarget, useDrop } from "react-dnd"
import { NativeTypes } from "react-dnd-html5-backend"

interface UseDragAndDropProps {
  onDrop: (file: File) => void
  enabled?: boolean
}

interface DragAndDropHook {
  dropRef: ConnectDropTarget
  isOver: boolean
  canDrop: boolean
}

export const useDragAndDrop = ({ onDrop, enabled = true }: UseDragAndDropProps): DragAndDropHook => {
  const handleDrop = useCallback(
    (item: { files: File[] }) => {
      if (!enabled) return false

      const file = item.files[0]
      if (!file) return false

      onDrop(file)
      return true
    },
    [onDrop, enabled]
  )

  const [{ isOver, canDrop }, dropRef] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop: (item: { files: File[] }) => handleDrop(item),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop() && enabled,
      }),
      canDrop: (item: { files: File[] }) => {
        const file = item.files[0]
        return enabled && Boolean(file)
      },
    }),
    [handleDrop, enabled]
  )

  return {
    dropRef,
    isOver,
    canDrop,
  }
}
